import React from "react";
import { faExternalLink } from "@fortawesome/pro-regular-svg-icons";
import { faCloud, faServer } from "@fortawesome/pro-solid-svg-icons";
import { getAllActivePublications } from "@plugins/gatsby-source-paligo/src/config";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@singlestore/fusion/components/select/select";
import { cx } from "class-variance-authority";

import { FaIcon } from "components/FaIcon/FaIcon";

import "./VersionSelector.scss";

const publications = getAllActivePublications();

const VERSIONS_KEY_VAL: { [key: string]: string } = Object.fromEntries(
    publications.flatMap((publicationConfig) => {
        const allVersions = [
            publicationConfig.currentVersion,
            ...publicationConfig.pastVersions,
            ...publicationConfig.archivedVersions,
        ];

        return allVersions.map((version) => {
            let label = publicationConfig.shortName;

            if (publicationConfig.pastVersions.length > 0) {
                label = `${label} v${version}`;
            }

            return [`${publicationConfig.slug}_v${version}`, label];
        });
    })
);

type VersionSelectorProps = {
    value: string;
    onValueChange: (value: string) => void;
    className?: string;
    contentClassName?: string;
    hasAllOption?: boolean;
    loading?: boolean;
    includeArchivedVersions?: boolean;
};

export function VersionSelector({
    value,
    onValueChange,
    className,
    contentClassName,
    loading,
    hasAllOption = false,
    includeArchivedVersions = false,
}: VersionSelectorProps) {
    let selectOptions = publications.map((publicationConfig) => {
        let currentLabel = publicationConfig.shortName;

        if (publicationConfig.pastVersions.length > 0) {
            currentLabel = `${currentLabel} v${publicationConfig.currentVersion}`;
        }

        let archivedOptions;

        if (includeArchivedVersions) {
            archivedOptions = publicationConfig.archivedVersions
                .sort()
                .reverse()
                .map((pastVersion) => {
                    const value = `${publicationConfig.slug}_v${pastVersion}`;

                    return (
                        <SelectItem
                            key={value}
                            value={value}
                            className="archived"
                        >
                            <div>{VERSIONS_KEY_VAL[value]}</div>
                            <FaIcon icon={faExternalLink} />
                        </SelectItem>
                    );
                });
        }

        return (
            <React.Fragment key={publicationConfig.slug}>
                <SelectItem
                    key={`${publicationConfig.slug}_v${publicationConfig.currentVersion}`}
                    value={`${publicationConfig.slug}_v${publicationConfig.currentVersion}`}
                >
                    {currentLabel}
                </SelectItem>
                {publicationConfig.pastVersions
                    .sort()
                    .reverse()
                    .map((pastVersion) => {
                        const value = `${publicationConfig.slug}_v${pastVersion}`;

                        return (
                            <SelectItem key={value} value={value}>
                                {VERSIONS_KEY_VAL[value]}
                            </SelectItem>
                        );
                    })}
                {archivedOptions}
            </React.Fragment>
        );
    });

    let icon: Maybe<JSX.Element>;

    if (value?.startsWith("cloud")) {
        icon = <FaIcon icon={faCloud} mr="0-25x" />;
    } else if (value?.startsWith("db")) {
        icon = <FaIcon icon={faServer} mr="0-25x" />;
    }

    const classes = cx("version-selector", className);

    let allOption;

    if (hasAllOption) {
        allOption = (
            <SelectItem key="all" value="all">
                All
            </SelectItem>
        );
    }

    let triggerContent = (
        <>
            {icon}
            <SelectValue aria-label={VERSIONS_KEY_VAL[value]} />
        </>
    );

    if (loading) {
        triggerContent = <>Loading...</>;
    }

    const contentClasses = cx("versions-selector-content", contentClassName);

    return (
        <Select value={value} onValueChange={onValueChange}>
            <SelectTrigger
                aria-label="Select SingleStoreDB documentation version"
                className={classes}
            >
                {triggerContent}
            </SelectTrigger>

            <SelectContent className={contentClasses}>
                {allOption}
                {selectOptions}
            </SelectContent>
        </Select>
    );
}
